import classNames from "classnames";
import type {
    AgentSkill,
    AgentSkillRevision,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { Button } from "@design-system/Button/Button";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { formatTokenDate } from "@helpers/date";
import { actorLabel, groupAgentSkillRevisions } from "./agentSkillChat";
import styles from "./AgentSkills.module.scss";

interface Props {
    skill: Pick<AgentSkill, "id" | "defaultSkillId">;
    revisions: AgentSkillRevision[];
    isFetched: boolean;
    currentRevisionId: string | null;
    canManage: boolean;
    /** Loads the version into the editor; absent when there is no editor. */
    onView?: (revision: AgentSkillRevision) => void;
    onRestore: (revision: AgentSkillRevision) => Promise<unknown>;
}

export const AgentSkillRevisionList = ({
    skill,
    revisions,
    isFetched,
    currentRevisionId,
    canManage,
    onView,
    onRestore,
}: Props) => {
    if (!isFetched) {
        return <Loader />;
    }
    if (revisions.length === 0) {
        return <EmptyState header="No versions yet" />;
    }
    return (
        <VStack>
            {groupAgentSkillRevisions(revisions, {
                skillId: skill.id,
                defaultSkillId: skill.defaultSkillId,
            }).flatMap((group) => [
                group.label !== null && (
                    <span
                        key={group.key}
                        className={classNames(
                            "text-secondary",
                            styles.revisionGroupHeading,
                        )}
                    >
                        {group.label}
                    </span>
                ),
                ...group.revisions.map((revision) => (
                    <HStack
                        key={revision.id}
                        className={styles.revisionRow}
                        gap={12}
                        align="center"
                        justify="between"
                    >
                        <VStack gap={2}>
                            <HStack gap={8} align="center">
                                <span>Version {revision.version}</span>
                                {revision.id === currentRevisionId && (
                                    <Tag color="accent" size="sm">
                                        Current
                                    </Tag>
                                )}
                                {revision.isArchived &&
                                    !revision.isArchivedSkill && (
                                        <Tag color="neutral" size="sm">
                                            Deleted
                                        </Tag>
                                    )}
                                {!revision.isEnabled &&
                                    !revision.isArchived && (
                                        <Tag color="neutral" size="sm">
                                            Disabled
                                        </Tag>
                                    )}
                            </HStack>
                            <span className="text-secondary">
                                {actorLabel(revision)} ·{" "}
                                {formatTokenDate(revision.createdAt)}
                            </span>
                        </VStack>
                        <HStack gap={8}>
                            {onView && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => onView(revision)}
                                >
                                    View
                                </Button>
                            )}
                            {canManage && revision.id !== currentRevisionId && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => void onRestore(revision)}
                                >
                                    Restore
                                </Button>
                            )}
                        </HStack>
                    </HStack>
                )),
            ])}
        </VStack>
    );
};
